import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Warehouse, Plus, Trash2, Edit2, Loader2, MapPin, Package, ClipboardList, ShieldCheck, Check, X as XIcon, History, ClipboardEdit, ClipboardCheck, ScanLine, Tag, Boxes, RefreshCw, FileDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';
import { useAdminAuth } from '../context/AdminAuthContext';
import { formatDate, formatDateTime } from '../lib/dateFormat';
import WarehouseTaskScanner from '../components/WarehouseTaskScanner';
import BarcodeScannerModal from '../components/BarcodeScannerModal';
import OrderComboBox from '../components/OrderComboBox';
import PalletLabelsTab from '../components/PalletLabelsTab';
import Rial from '../components/Rial';
import AdminLayout from '../components/AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminWarehouses — warehouse CRUD + batch placement + stock view.
 * FEFO planning is triggered from the orders page (auto on "confirmed"
 * status change + a manual Recalculate button) so this page focuses on
 * the *warehouse-side* setup: create warehouses, rename them, and place
 * batches into specific Zone / Location / Pallet coordinates.
 */

export default function AdminWarehouses() {
  const { token } = useAdminAuth();
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [tab, setTab] = useState('warehouses'); // 'warehouses' | 'placements' | 'stock' | 'transfers' | 'alerts' | 'audit'
  const [me, setMe] = useState(null); // warehouse role context
  const [warehouses, setWarehouses] = useState([]);
  const [placements, setPlacements] = useState([]);
  const [selectedWhId, setSelectedWhId] = useState('');
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [newPriority, setNewPriority] = useState(100);
  const [editWhId, setEditWhId] = useState('');
  const [editName, setEditName] = useState('');
  const [editPriority, setEditPriority] = useState(100);
  // Feb 2026 — Mandatory ASN/Excel Entry toggle. Super-admin only.
  // When ON, manual "Add Pallet" writes to this warehouse are blocked.
  const [editMandatoryAsnEntry, setEditMandatoryAsnEntry] = useState(false);
  const [saving, setSaving] = useState(null);
  // Feb 2026 — track which warehouse rows are currently PATCHing a
  // category toggle so the chip disables + spinners while the request
  // is in flight.
  const [savingCategoryFor, setSavingCategoryFor] = useState('');
  // Feb 2026 — category-assignment auto-transfer flow. When a super
  // admin turns ON a category, the UI first calls the preview endpoint
  // and pops a confirm dialog before saving the assignment + moving
  // any pre-existing stock of that category into this warehouse.
  const [transferPreview, setTransferPreview] = useState(null); // preview payload
  const [transferPending, setTransferPending] = useState(null); // { warehouse, slug, nextSlugs }
  const [transferBusy, setTransferBusy] = useState(false);
  const { toast } = useToast();

  // Feb 2026 v2 — Warehouse features are now per-user. The
  // `/admin/warehouses/me` endpoint returns a `warehouse_features_denied`
  // list; each entry hides the matching tab for THIS user. Super admin
  // bypasses the list entirely (server also omits it for super admins).
  const isSuper = !!(me && me.is_super_admin);
  const featureVisible = (fid) => {
    if (isSuper) return true;
    const denied = (me && me.warehouse_features_denied) || [];
    return !denied.includes(fid);
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [w, pl, pr, meRes, cats] = await Promise.all([
        axios.get(`${API}/admin/warehouses`, { headers: authHeaders }),
        axios.get(`${API}/admin/warehouses/placements`, { headers: authHeaders }),
        axios.get(`${API}/products?limit=500`, { headers: authHeaders }),
        axios.get(`${API}/admin/warehouses/me`, { headers: authHeaders }).catch(() => ({ data: null })),
        axios.get(`${API}/admin/categories`, { headers: authHeaders }).catch(() => ({ data: [] })),
      ]);
      setWarehouses(w.data.warehouses || []);
      setPlacements(pl.data.placements || []);
      setProducts((Array.isArray(pr.data) ? pr.data : pr.data.products) || []);
      setMe(meRes.data || null);
      setCategories(cats.data || []);
      if (!selectedWhId && (w.data.warehouses || []).length > 0) {
        setSelectedWhId(w.data.warehouses[0].id);
      }
    } catch (e) {
      toast({ title: 'Failed to load', description: e.response?.data?.detail || 'Try again.' });
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const createWarehouse = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      await axios.post(`${API}/admin/warehouses`, { name: newName.trim(), priority: Number(newPriority) || 100 }, { headers: authHeaders });
      toast({ title: 'Warehouse created', description: newName.trim() });
      setNewName(''); setNewPriority(100);
      await loadAll();
    } catch (e) {
      toast({ title: 'Create failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setCreating(false); }
  };

  const startEdit = (w) => {
    setEditWhId(w.id);
    setEditName(w.name);
    setEditPriority(w.priority ?? 100);
    setEditMandatoryAsnEntry(!!w.mandatory_asn_entry);
  };
  const cancelEdit = () => {
    setEditWhId('');
    setEditName('');
    setEditMandatoryAsnEntry(false);
  };

  // Feb 2026 — Forward-only category mapping. Turning a category on
  // or off is now saved immediately — NO existing stock is moved. The
  // assignment is a routing rule for new inbound stock only (ASN
  // receiving + Excel bulk-upload park new pallets on the mapped
  // warehouse). To move historic stock, use the Transfers tab.
  const toggleWarehouseCategory = async (w, slug) => {
    const currentlyOn = (w.category_slugs || []).includes(slug);
    const cur = new Set(w.category_slugs || []);
    if (currentlyOn) cur.delete(slug); else cur.add(slug);
    const nextSlugs = Array.from(cur);
    setSavingCategoryFor(`${w.id}:${slug}`);
    setWarehouses((prev) => prev.map((row) => (
      row.id === w.id ? { ...row, category_slugs: nextSlugs } : row
    )));
    try {
      await axios.put(
        `${API}/admin/warehouses/${w.id}`,
        { category_slugs: nextSlugs },
        { headers: authHeaders },
      );
      toast({
        title: currentlyOn ? 'Category removed' : 'Category assigned',
        description: currentlyOn
          ? `Future stock for that category will no longer route to ${w.name}.`
          : `New ASN receipts and Excel uploads for that category will now land in ${w.name}. No existing stock was moved.`,
      });
    } catch (e) {
      setWarehouses((prev) => prev.map((row) => (
        row.id === w.id ? { ...row, category_slugs: w.category_slugs || [] } : row
      )));
      toast({ title: 'Category save failed', description: e.response?.data?.detail || 'Try again.' });
    } finally {
      setSavingCategoryFor('');
    }
  };

  const cancelCategoryTransfer = () => {
    setTransferPreview(null);
    setTransferPending(null);
  };

  const applyCategoryTransfer = async () => {
    // Legacy handler retained so the modal (if ever re-opened via a
    // stale route) still closes cleanly. Forward-only policy means we
    // never call the transfer endpoint from the UI anymore.
    cancelCategoryTransfer();
  };

  const saveEdit = async () => {
    setSaving(editWhId);
    try {
      const body = { name: editName.trim(), priority: Number(editPriority) || 100 };
      // Only super admin can flip the Mandatory ASN toggle; backend
      // rejects the field otherwise so we skip it for non-supers.
      if (isSuper) {
        body.mandatory_asn_entry = !!editMandatoryAsnEntry;
      }
      await axios.put(`${API}/admin/warehouses/${editWhId}`, body, { headers: authHeaders });
      toast({ title: 'Warehouse updated' });
      cancelEdit(); await loadAll();
    } catch (e) {
      toast({ title: 'Rename failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSaving(null); }
  };

  const deleteWh = async (w) => {
    if (!window.confirm(`Delete warehouse "${w.name}"? Placements linked to it must be moved or removed first.`)) return;
    setSaving(w.id);
    try {
      await axios.delete(`${API}/admin/warehouses/${w.id}`, { headers: authHeaders });
      toast({ title: 'Deleted' });
      if (selectedWhId === w.id) setSelectedWhId('');
      await loadAll();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSaving(null); }
  };

  const deletePlacement = async (id) => {
    if (!window.confirm('Remove this placement? The batch qty will revert to Unassigned in the Default Warehouse.')) return;
    setSaving(id);
    try {
      await axios.delete(`${API}/admin/warehouses/placements/${id}`, { headers: authHeaders });
      toast({ title: 'Placement removed' });
      await loadAll();
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setSaving(null); }
  };

  const warehousesById = useMemo(() => Object.fromEntries(warehouses.map((w) => [w.id, w])), [warehouses]);
  const productsById   = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])),     [products]);

  // Super-admin-only: download a portrait-A4 sitemap PDF covering every
  // tab, feature and role permission inside this page. The endpoint
  // enforces super-admin server-side too so a manual URL hit still 403s.
  const [downloadingSitemap, setDownloadingSitemap] = useState(false);
  const downloadWmsSitemapPdf = async () => {
    setDownloadingSitemap(true);
    try {
      const res = await axios.get(`${API}/admin/warehouses/wms-sitemap.pdf`, {
        headers: authHeaders,
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T-]/g, '');
      a.download = `wms-sitemap-${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Download failed', description: e.response?.data?.detail || 'Try again.' });
    } finally {
      setDownloadingSitemap(false);
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-6" data-testid="admin-warehouses-page">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <Warehouse className="w-6 h-6 text-[#0078D4]" />
        <h1 className="text-2xl font-bold text-[#0F172A]">Warehouses &amp; FEFO dispatch</h1>
        {isSuper && (
          <button
            type="button"
            onClick={downloadWmsSitemapPdf}
            disabled={downloadingSitemap}
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-[#0078D4] text-[#0078D4] hover:bg-[#0078D4]/5 disabled:opacity-60 text-xs font-bold"
            data-testid="admin-warehouses-download-wms-sitemap-pdf"
            title="Super-admin only. Downloads a printable sitemap of every tab, feature and role permission in this module."
          >
            {downloadingSitemap ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileDown className="w-4 h-4" />
            )}
            Download WMS Sitemap PDF
          </button>
        )}
        <a
          href="/warehouse/floor"
          className={`${isSuper ? '' : 'ml-auto '}inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-slate-950 hover:bg-black text-white text-xs font-bold`}
          data-testid="admin-warehouses-open-floor-mode"
          title="Mobile-optimised floor operations view — designed for phones on the warehouse floor. Share the URL /warehouse/floor with staff."
        >
          <ScanLine className="w-4 h-4" /> Open floor mode →
        </a>
      </div>
      <p className="text-sm text-neutral-600 mb-6">
        Manage warehouses, place batches into specific Zone / Location / Pallet coordinates, and view live stock per warehouse. FEFO dispatch planning runs automatically the moment an order is confirmed — head to the order detail page for the pick list.
      </p>

      {me && (me.is_super_admin || me.role) && (
        <div className="mb-4 flex items-center gap-2 text-xs bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2" data-testid="admin-warehouses-me-banner">
          <ShieldCheck className="w-4 h-4 text-indigo-700" />
          <span className="font-semibold text-indigo-800">
            Signed in as{' '}
            {me.is_super_admin
              ? 'Super Admin (all roles + global scope)'
              : `${(me.role || '').replace('_', ' ')}`}
          </span>
          {!me.is_super_admin && me.role !== 'manager' && (
            <span className="text-indigo-700">
              · Scope:{' '}
              {(me.scope || []).includes('*') || (me.scope || []).length === 0
                ? 'All warehouses'
                : (me.scope_warehouses || []).map((w) => w.name).join(', ')}
            </span>
          )}
        </div>
      )}

      <div className="flex gap-2 mb-4 border-b border-neutral-200">
        {[
          ['warehouses', 'Warehouses', Warehouse],
          featureVisible('placements') && ['placements', 'Placements', MapPin],
          featureVisible('stock')      && ['stock',      'Stock by warehouse', Package],
          // Feb 2026 — the global Tasks tab has been removed. Task
          // assignment now lives on each order card on Admin Orders
          // via the WmsTaskAssignPanel component. Keeping the feature
          // flag so RBAC records stay stable; simply not rendering it.
          // Feb 2026 — the global Loading tab has been removed as well.
          // Per-order loading assignment lives on the same
          // WmsTaskAssignPanel (the "Loading" row alongside Picking
          // and Put-away). The Ready-to-load queue is now the natural
          // Admin Orders list filtered by `status=picked_up`.
          featureVisible('pallet_labels') && ['pallet-labels', 'Pallet labels', Tag],
          featureVisible('transfers')  && ['transfers',  'Transfers', ClipboardList],
          featureVisible('alerts')     && ['alerts',     'Replenishment alerts', ClipboardList],
          ...((featureVisible('requisitions') && me && (me.is_super_admin || me.role === 'manager' || me.role === 'floor_incharge')) ? [['requisitions', 'Requisitions', ClipboardEdit]] : []),
          ...(me && (me.is_super_admin || me.role === 'manager') ? [['audit', 'Audit log', History]] : []),
        ].filter(Boolean).map(([k, label, Icon]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 ${tab === k ? 'border-[#0078D4] text-[#0078D4]' : 'border-transparent text-neutral-600 hover:text-[#0F172A]'}`}
            data-testid={`admin-warehouses-tab-${k}`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="text-center py-12 text-neutral-500">
          <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" /> Loading…
        </div>
      )}

      {!loading && tab === 'warehouses' && (
        <div>
          <div className="rounded-2xl border border-neutral-200 bg-white p-4 mb-4">
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2 font-semibold">New warehouse</div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Warehouse name (e.g. Muscat Central)"
                className="text-sm flex-1"
                data-testid="admin-warehouse-new-name"
              />
              <Input
                type="number"
                value={newPriority}
                onChange={(e) => setNewPriority(e.target.value)}
                placeholder="Priority (lower = preferred)"
                className="text-sm w-full sm:w-52"
                data-testid="admin-warehouse-new-priority"
              />
              <Button
                onClick={createWarehouse}
                disabled={creating || !newName.trim()}
                className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full"
                data-testid="admin-warehouse-create-btn"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create</>}
              </Button>
            </div>
            <p className="text-[11px] text-neutral-500 mt-1">Priority is used only for FEFO tie-breaks when two warehouses hold stock with the same earliest expiry.</p>
          </div>

          <div className="space-y-2">
            {warehouses.map((w) => (
              <div
                key={w.id}
                className={`rounded-2xl border p-3 ${w.is_default ? 'border-amber-200 bg-amber-50/40' : 'border-neutral-200 bg-white'}`}
                data-testid={`admin-warehouse-row-${w.id}`}
              >
                <div className="flex items-center gap-3">
                  <Warehouse className={`w-5 h-5 ${w.is_default ? 'text-amber-600' : 'text-[#0078D4]'}`} />
                  <div className="flex-1 min-w-0">
                    {editWhId === w.id ? (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="text-sm flex-1" data-testid={`admin-warehouse-edit-name-${w.id}`} />
                          <Input type="number" value={editPriority} onChange={(e) => setEditPriority(e.target.value)} className="text-sm w-24" data-testid={`admin-warehouse-edit-priority-${w.id}`} />
                        </div>
                        {isSuper && !w.is_default && (
                          <label
                            className="flex items-start gap-2 text-xs text-[#0F172A] bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2 cursor-pointer select-none"
                            data-testid={`admin-warehouse-mandatory-asn-label-${w.id}`}
                          >
                            <input
                              type="checkbox"
                              checked={!!editMandatoryAsnEntry}
                              onChange={(e) => setEditMandatoryAsnEntry(e.target.checked)}
                              className="mt-0.5 w-4 h-4 accent-amber-600"
                              data-testid={`admin-warehouse-mandatory-asn-toggle-${w.id}`}
                            />
                            <span className="leading-snug">
                              <span className="font-semibold">Mandatory ASN / Excel Entry</span>
                              <span className="block text-[10px] text-amber-800 mt-0.5">
                                When ON, manual "Add Pallet" and direct placement writes to this warehouse are disabled — stock must enter via the ASN module or an Excel upload.
                              </span>
                            </span>
                          </label>
                        )}
                      </div>
                    ) : (
                      <div>
                        <div className="font-semibold text-sm text-[#0F172A] flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span>{w.name}</span>
                          {w.is_default && <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full">Default · holds unassigned stock</span>}
                          {w.mandatory_asn_entry && (
                            <span
                              className="text-[10px] uppercase tracking-wider text-purple-800 bg-purple-100 border border-purple-200 px-1.5 py-0.5 rounded-full"
                              data-testid={`admin-warehouse-mandatory-asn-badge-${w.id}`}
                              title="Manual pallet writes disabled — stock must enter via ASN or Excel upload."
                            >
                              ASN/Excel only
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-neutral-500">Priority: {w.priority ?? 100}</div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {editWhId === w.id ? (
                      <>
                        <Button size="sm" onClick={saveEdit} disabled={saving === w.id} className="bg-[#0078D4] text-white rounded-full" data-testid={`admin-warehouse-save-${w.id}`}>Save</Button>
                        <Button size="sm" variant="outline" onClick={cancelEdit} className="rounded-full">Cancel</Button>
                      </>
                    ) : (
                      <>
                        {isSuper && (
                          <Button size="sm" variant="outline" onClick={() => startEdit(w)} className="rounded-full" data-testid={`admin-warehouse-edit-btn-${w.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        )}
                        {isSuper && !w.is_default && (
                          <Button size="sm" variant="outline" onClick={() => deleteWh(w)} disabled={saving === w.id} className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid={`admin-warehouse-delete-btn-${w.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                {/* Feb 2026 — category-based access control. Super admin
                    can single-click toggle each category on/off. Sub-admins
                    (and readers) see the chips read-only so they know
                    which category tags the warehouse carries. */}
                {categories.length > 0 && !w.is_default && (
                  <div
                    className="mt-2 flex flex-wrap items-center gap-1.5"
                    data-testid={`admin-warehouse-categories-${w.id}`}
                  >
                    <Tag className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mr-1">Categories</span>
                    {categories.map((c) => {
                      const active = (w.category_slugs || []).includes(c.slug);
                      const busy = savingCategoryFor === `${w.id}:${c.slug}`;
                      const testid = `admin-warehouse-category-chip-${w.id}-${c.slug}`;
                      return (
                        <button
                          key={c.slug}
                          type="button"
                          onClick={() => isSuper && toggleWarehouseCategory(w, c.slug)}
                          disabled={!isSuper || busy}
                          className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border transition inline-flex items-center gap-1 ${
                            active
                              ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                              : 'bg-white border-neutral-200 text-neutral-500 hover:border-neutral-300'
                          } ${isSuper ? 'cursor-pointer' : 'cursor-default'} disabled:opacity-60`}
                          data-testid={testid}
                          title={isSuper
                            ? `Click to ${active ? 'remove' : 'add'} “${c.name}” access`
                            : `${c.name} — ${active ? 'granted' : 'not granted'}`}
                        >
                          {active && <Check className="w-3 h-3" />}
                          {c.name}
                        </button>
                      );
                    })}
                    {(w.category_slugs || []).length === 0 && (
                      <span
                        className="text-[10px] italic text-neutral-400 ml-1"
                        data-testid={`admin-warehouse-categories-empty-${w.id}`}
                      >
                        No category restriction — visible to every warehouse user
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && tab === 'placements' && (
        <PlacementsTab
          products={products}
          warehouses={warehouses.filter((w) => !w.is_default)}
          allWarehouses={warehouses}
          placements={placements}
          productsById={productsById}
          warehousesById={warehousesById}
          onDelete={deletePlacement}
          onReload={loadAll}
          authHeaders={authHeaders}
        />
      )}

      {!loading && tab === 'stock' && (
        <StockByWarehouseTab
          warehouses={warehouses}
          selectedWhId={selectedWhId}
          onSelect={setSelectedWhId}
          authHeaders={authHeaders}
        />
      )}

      {!loading && tab === 'transfers' && (
        <TransfersTab
          warehouses={warehouses}
          placements={placements}
          products={products}
          warehousesById={warehousesById}
          authHeaders={authHeaders}
          onReload={loadAll}
        />
      )}

      {!loading && tab === 'alerts' && (
        <AlertsTab authHeaders={authHeaders} />
      )}

      {!loading && tab === 'requisitions' && (
        <RequisitionsTab authHeaders={authHeaders} />
      )}

      {/* Feb 2026 — Tasks tab retired. See WmsTaskAssignPanel on Admin
          Orders for per-order assignment. `TasksTab` component below is
          kept in-file (unused) so we can roll back with one flip if the
          new per-order panel misses an edge case. */}

      {/* Feb 2026 — Loading tab retired. Per-order loading assignment
          now lives on the WmsTaskAssignPanel next to the Driver panel
          on each Admin Orders card. `LoadingTab` component below is
          kept in-file (unused) so we can roll back with one flip if
          the new per-order panel misses an edge case. */}

      {!loading && tab === 'pallet-labels' && (
        <PalletLabelsTab authHeaders={authHeaders} />
      )}

      {!loading && tab === 'audit' && (
        <AuditLogTab authHeaders={authHeaders} warehousesById={warehousesById} />
      )}

      {/* Feb 2026 — Category-assignment auto-transfer confirmation. */}
      {transferPreview && transferPending && (
        <CategoryTransferModal
          preview={transferPreview}
          pending={transferPending}
          busy={transferBusy}
          onCancel={cancelCategoryTransfer}
          onConfirm={applyCategoryTransfer}
        />
      )}
      </div>
    </AdminLayout>
  );
}

/**
 * CategoryTransferModal — confirms an "assign category to warehouse"
 * action, including the automatic transfer of any existing stock of
 * that category into the target warehouse.
 *
 * Behaviour:
 *   * All UNALLOCATED placements of the target category move to the
 *     destination on confirm.
 *   * Placements with `allocated_qty > 0` (reserved on active orders)
 *     are ALWAYS SKIPPED — surfaced in a red-flagged list so ops can
 *     see which SKUs are held until the reservations ship. Those
 *     placements migrate later, either the next time the category is
 *     re-assigned or via a manual transfer.
 */
const CategoryTransferModal = ({
  preview,
  pending,
  busy,
  onCancel,
  onConfirm,
}) => {
  const hasStock = (preview?.placement_count || 0) > 0;
  const movable = preview?.movable_placement_count || 0;
  const flagged = preview?.flagged_items || [];
  const hasFlagged = flagged.length > 0;
  const wName = pending?.warehouse?.name || '';
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      data-testid="category-transfer-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b border-neutral-100 sticky top-0 bg-white z-10">
          <div className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1">
            Assign category · confirm auto-transfer
          </div>
          <div className="font-display text-lg font-bold text-[#0F172A]">
            {wName} · <span className="text-[#0078D4] font-mono">{preview?.category_slug || pending?.slug}</span>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {!hasStock && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="category-transfer-nothing-to-move">
              No existing stock of this category to transfer. The category will simply be tagged on this warehouse.
            </div>
          )}
          {hasStock && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <StatTile label="SKUs" value={preview.sku_count} testid="category-transfer-sku-count" />
                <StatTile label="Placements" value={preview.placement_count} testid="category-transfer-placement-count" />
                <StatTile label="Qty to move" value={preview.movable_qty} testid="category-transfer-movable-qty" accent />
                <StatTile label="Qty held (allocated)" value={preview.allocated_qty} testid="category-transfer-allocated-qty" warn={hasFlagged} />
              </div>
              {(preview.staged_placement_count || 0) > 0 && (
                <div
                  className="rounded-lg border border-indigo-200 bg-indigo-50 p-3"
                  data-testid="category-transfer-staged-summary"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-bold text-indigo-900 uppercase tracking-wider">
                      Includes STAGE stock from Default
                    </div>
                    <div className="text-[11px] font-semibold text-indigo-800 tabular-nums">
                      <span data-testid="category-transfer-staged-sku-count">{preview.staged_sku_count}</span> SKU{preview.staged_sku_count === 1 ? '' : 's'}
                      &nbsp;·&nbsp;
                      <span data-testid="category-transfer-staged-qty">{preview.staged_qty}</span> units
                      &nbsp;·&nbsp;
                      <span data-testid="category-transfer-staged-placement-count">{preview.staged_placement_count}</span> placement{preview.staged_placement_count === 1 ? '' : 's'}
                    </div>
                  </div>
                  <div className="text-[11px] text-indigo-800 mt-1 leading-relaxed">
                    Bulk-uploaded stock still sitting in the Default warehouse&apos;s STAGE zone will migrate too — it&apos;s counted in the numbers above.
                  </div>
                </div>
              )}
              {(preview.source_warehouses || []).length > 0 && (
                <div className="rounded-lg border border-neutral-200 bg-neutral-50/60 p-3" data-testid="category-transfer-sources">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-neutral-500 mb-2">
                    Source warehouses (unallocated only)
                  </div>
                  <ul className="space-y-1 text-sm">
                    {preview.source_warehouses.map((s) => (
                      <li key={s.id} className="flex items-center justify-between tabular-nums" data-testid={`category-transfer-source-${s.id}`}>
                        <span className="text-neutral-800">{s.name || s.id.slice(0, 8)}</span>
                        <span className="text-neutral-500 text-xs">
                          <span className="font-semibold text-neutral-800">{s.qty}</span> units · {s.placement_count} placement{s.placement_count === 1 ? '' : 's'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {hasFlagged && (
                <div
                  className="rounded-xl border border-red-200 bg-red-50 p-3"
                  data-testid="category-transfer-flagged-list"
                >
                  <div className="text-sm font-bold text-red-900 flex items-center gap-1.5">
                    ⚠ {flagged.length} SKU{flagged.length === 1 ? '' : 's'} held · {preview.allocated_placement_count} placement{preview.allocated_placement_count === 1 ? '' : 's'}
                  </div>
                  <div className="text-xs text-red-800 mt-1 mb-2 leading-relaxed">
                    These items have open allocations on active orders. They&apos;ll be skipped for now and stay in their current warehouse until the allocations are fulfilled.
                  </div>
                  <ul className="space-y-1 max-h-32 overflow-y-auto text-xs text-red-900 divide-y divide-red-100">
                    {flagged.map((f) => (
                      <li
                        key={f.product_id}
                        className="py-1 flex items-center justify-between tabular-nums gap-2"
                        data-testid={`category-transfer-flagged-item-${f.product_id}`}
                      >
                        <span className="font-semibold truncate">
                          {f.product_sku && <span className="font-mono text-[10px] text-red-600 mr-1">{f.product_sku}</span>}
                          {f.product_name || f.product_id.slice(0, 8)}
                        </span>
                        <span className="text-red-700 shrink-0">
                          <span className="font-bold">{f.allocated_qty}</span> allocated · {f.held_qty} held
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
        <div className="p-5 border-t border-neutral-100 sticky bottom-0 bg-white flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="text-xs font-semibold text-neutral-600 hover:text-neutral-900 px-3 py-2 rounded-full border border-neutral-300 hover:bg-neutral-50 disabled:opacity-50"
            data-testid="category-transfer-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="text-xs font-bold text-white px-4 py-2 rounded-full bg-[#0078D4] hover:bg-[#005A9E] disabled:opacity-60 inline-flex items-center gap-1.5"
            data-testid="category-transfer-confirm-btn"
          >
            {busy
              ? 'Moving stock…'
              : movable > 0
                ? `Assign · move ${movable} placement${movable === 1 ? '' : 's'}${hasFlagged ? ' (skip held)' : ''}`
                : hasFlagged
                  ? 'Assign · all stock held'
                  : 'Assign category'}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatTile = ({ label, value, testid, accent, warn }) => (
  <div
    className={`rounded-lg border px-3 py-2 ${
      warn
        ? 'border-amber-200 bg-amber-50'
        : accent
        ? 'border-[#0078D4]/40 bg-[#EFF6FF]'
        : 'border-neutral-200 bg-white'
    }`}
    data-testid={testid}
  >
    <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">{label}</div>
    <div className="text-lg font-bold text-neutral-900 tabular-nums">{value}</div>
  </div>
);

const PlacementsTab = ({ products, warehouses, allWarehouses, placements, warehousesById, onDelete, onReload, authHeaders }) => {
  const [form, setForm] = useState({
    product_id: '', warehouse_id: '', production_date: '', expiry_date: '',
    zone: '', location_id: '', pallet_id: '', qty: '',
  });
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const patch = (u) => setForm((p) => ({ ...p, ...u }));

  const create = async () => {
    if (!form.product_id || !form.warehouse_id || !form.production_date || !form.qty) {
      toast({ title: 'Missing fields', description: 'Product, warehouse, production date and qty are required.' });
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/warehouses/placements`, {
        ...form, qty: Number(form.qty) || 0,
      }, { headers: authHeaders });
      toast({ title: 'Placement created' });
      setForm({ product_id: '', warehouse_id: '', production_date: '', expiry_date: '', zone: '', location_id: '', pallet_id: '', qty: '' });
      onReload();
    } catch (e) {
      toast({ title: 'Create failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setCreating(false); }
  };

  const availableBatches = useMemo(() => {
    const p = products.find((x) => x.id === form.product_id);
    return (p?.batches || []).filter((b) => (b.production_date || '').trim() && (b.qty || 0) > 0);
  }, [products, form.product_id]);

  // Feb 2026 — When the selected warehouse has 'Mandatory ASN/Excel
  // Entry' turned on, manual placement is refused server-side. Hide
  // the "Place" button and show an explanatory banner so ops never
  // hits a mysterious 403.
  const selectedWarehouse = useMemo(
    () => warehouses.find((x) => x.id === form.warehouse_id) || null,
    [warehouses, form.warehouse_id],
  );
  const mandatoryAsnOnSelected = !!(selectedWarehouse && selectedWarehouse.mandatory_asn_entry);

  return (
    <div>
      <div className="rounded-2xl border border-neutral-200 bg-white p-4 mb-4">
        <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2 font-semibold">Place a batch</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <select value={form.product_id} onChange={(e) => patch({ product_id: e.target.value, production_date: '', expiry_date: '' })} className="text-sm rounded-md border border-neutral-300 px-3 py-2" data-testid="admin-placement-product-select">
            <option value="">Product…</option>
            {products.filter((p) => (p.batches || []).length > 0).map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select value={form.production_date} onChange={(e) => {
            const b = availableBatches.find((x) => x.production_date === e.target.value);
            patch({ production_date: e.target.value, expiry_date: b?.expiry_date || '' });
          }} className="text-sm rounded-md border border-neutral-300 px-3 py-2" data-testid="admin-placement-batch-select">
            <option value="">Batch (production date)…</option>
            {availableBatches.map((b) => (
              <option key={b.production_date} value={b.production_date}>
                {formatDate(b.production_date)} · exp {formatDate(b.expiry_date) || '—'} · qty {b.qty}
              </option>
            ))}
          </select>
          <select value={form.warehouse_id} onChange={(e) => patch({ warehouse_id: e.target.value })} className="text-sm rounded-md border border-neutral-300 px-3 py-2" data-testid="admin-placement-warehouse-select">
            <option value="">Warehouse…</option>
            {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <Input value={form.qty} onChange={(e) => patch({ qty: e.target.value })} placeholder="Qty" type="number" className="text-sm" data-testid="admin-placement-qty" />
          <Input value={form.zone} onChange={(e) => patch({ zone: e.target.value })} placeholder="Zone (leave blank → Stage)" className="text-sm" data-testid="admin-placement-zone" />
          <Input value={form.location_id} onChange={(e) => patch({ location_id: e.target.value })} placeholder="Location ID (leave blank → Stage)" className="text-sm" data-testid="admin-placement-location" />
          <Input value={form.pallet_id} onChange={(e) => patch({ pallet_id: e.target.value })} placeholder="Pallet ID (e.g. PLT-001)" className="text-sm" data-testid="admin-placement-pallet" />
          <Button
            onClick={create}
            disabled={creating || mandatoryAsnOnSelected}
            className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full disabled:opacity-60"
            data-testid="admin-placement-create-btn"
            title={mandatoryAsnOnSelected
              ? `${selectedWarehouse?.name || 'This warehouse'} requires stock via ASN or Excel upload — manual placement is disabled.`
              : 'Place batch'}
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Place</>}
          </Button>
        </div>
        {mandatoryAsnOnSelected && (
          <p
            className="text-[11px] text-purple-800 bg-purple-50 border border-purple-200 rounded-md px-3 py-2 mt-2"
            data-testid="admin-placement-mandatory-asn-warning"
          >
            <span className="font-bold">{selectedWarehouse.name}</span> is set to <b>Mandatory ASN/Excel Entry</b>.
            Add stock via the <span className="font-semibold">Goods Receiving (ASN)</span> module or the <span className="font-semibold">Products → Bulk Upload</span> flow. Manual pallet creation is disabled for this warehouse.
          </p>
        )}
        {warehouses.length === 0 && (
          <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">
            Create at least one non-default warehouse first (see the &quot;Warehouses&quot; tab).
          </p>
        )}
        <p className="text-[11px] text-neutral-500 mt-2 leading-snug">
          <span className="font-bold text-amber-700">Stage default:</span> leave Zone + Location blank and the placement lands in the <span className="font-mono font-semibold">STAGE</span> zone with an auto-minted pallet. Stage stock is invisible to FEFO until a floor operator puts it away to a real location.
        </p>
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">Batch (prod date)</th>
              <th className="px-3 py-2">Expiry</th>
              <th className="px-3 py-2">Warehouse</th>
              <th className="px-3 py-2">Zone</th>
              <th className="px-3 py-2">Location</th>
              <th className="px-3 py-2">Pallet</th>
              <th className="px-3 py-2 text-right">In Hand</th>
              <th className="px-3 py-2 text-right">Allocated</th>
              <th className="px-3 py-2 text-right">Available</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {placements.length === 0 && (
              <tr><td colSpan={11} className="text-center py-6 text-neutral-400 text-sm">No placements yet. Use the form above to place a batch.</td></tr>
            )}
            {placements.map((p) => {
              const inHand = Number(p.qty || 0);
              const allocated = Number(p.allocated_qty || 0);
              const available = Math.max(0, inHand - allocated);
              const isStage = !!p.stage || (p.zone || '').toUpperCase() === 'STAGE';
              return (
              <tr key={p.id} className={`border-t border-neutral-100 ${isStage ? 'bg-amber-50/60' : ''}`} data-testid={`admin-placement-row-${p.id}`}>
                <td className="px-3 py-2">
                  {p.product_name}
                  {isStage && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-amber-200 text-amber-900 border border-amber-300"
                      title="Stage: not available for picking or allocation until put away to a permanent location."
                      data-testid={`admin-placement-stage-badge-${p.id}`}
                    >
                      Stage
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs">{formatDate(p.production_date)}</td>
                <td className="px-3 py-2 font-mono text-xs">{formatDate(p.expiry_date) || '—'}</td>
                <td className="px-3 py-2">{warehousesById[p.warehouse_id]?.name || p.warehouse_id}</td>
                <td className="px-3 py-2">{p.zone || '—'}</td>
                <td className="px-3 py-2">{p.location_id || '—'}</td>
                <td className="px-3 py-2">{p.pallet_id || '—'}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">{inHand}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-semibold ${allocated > 0 ? 'text-amber-700' : 'text-neutral-400'}`}>{allocated}</td>
                <td className={`px-3 py-2 text-right tabular-nums font-bold ${isStage ? 'text-amber-700' : (available > 0 ? 'text-emerald-700' : 'text-red-600')}`}>
                  {isStage ? <span title="Stage stock is not available for FEFO">—</span> : available}
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => onDelete(p.id)} className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid={`admin-placement-delete-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StockByWarehouseTab = ({ warehouses, selectedWhId, onSelect, authHeaders }) => {
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!selectedWhId) return;
    setLoading(true);
    axios.get(`${API}/admin/warehouses/${selectedWhId}/stock`, { headers: authHeaders })
      .then((r) => setStock(r.data.stock || []))
      .catch((e) => toast({ title: 'Load failed', description: e.response?.data?.detail || 'Try again.' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWhId]);

  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <label className="text-sm font-medium text-neutral-600">Warehouse:</label>
        <select value={selectedWhId} onChange={(e) => onSelect(e.target.value)} className="text-sm rounded-md border border-neutral-300 px-3 py-2" data-testid="admin-stock-warehouse-select">
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>{w.name}{w.is_default ? ' (Default)' : ''}</option>
          ))}
        </select>
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-stock-table">
          <thead className="bg-neutral-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">Batch (prod date)</th>
              <th className="px-3 py-2">Expiry</th>
              <th className="px-3 py-2">Zone</th>
              <th className="px-3 py-2">Location</th>
              <th className="px-3 py-2">Pallet</th>
              <th className="px-3 py-2 text-right">Qty</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-6 text-neutral-400"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…</td></tr>}
            {!loading && stock.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-neutral-400 text-sm">No stock in this warehouse.</td></tr>}
            {!loading && stock.map((s, i) => (
              <tr key={s.id || `virt-${i}`} className={`border-t border-neutral-100 ${s.unassigned ? 'bg-amber-50/30' : ''}`}>
                <td className="px-3 py-2">{s.product_name}</td>
                <td className="px-3 py-2 font-mono text-xs">{formatDate(s.production_date)}</td>
                <td className="px-3 py-2 font-mono text-xs">{formatDate(s.expiry_date) || '—'}</td>
                <td className="px-3 py-2">
                  {s.zone || <span className="text-[10px] text-amber-700 bg-amber-100 border border-amber-200 px-1.5 py-0.5 rounded-full">Unassigned</span>}
                </td>
                <td className="px-3 py-2">{s.location_id || '—'}</td>
                <td className="px-3 py-2">{s.pallet_id || '—'}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">{s.qty}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-neutral-500 mt-2">
        <ClipboardList className="inline w-3 h-3 mr-1" />
        Rows highlighted amber = batch qty not yet placed at a specific zone/location/pallet. FEFO dispatch treats them as &quot;Default Warehouse&quot; stock and marks each pick line &quot;Unassigned&quot;.
      </p>
    </div>
  );
};

// ============================================================
// TRANSFERS TAB (July 2026 phase-2)
// ============================================================
const TransfersTab = ({ warehouses, placements, products, warehousesById, authHeaders, onReload }) => {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const { toast } = useToast();
  const productsById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

  // Feb 2026 — Redesigned bulk-transfer UI.
  // The tab now leads with two mandatory dropdowns (From / To) and,
  // once a source is picked, auto-loads every product with free stock
  // in that warehouse. Rows are grouped by category with a Select-All
  // master toggle + per-category Select-All buttons + a
  // "Transfer Full Quantities" one-tap fill. Confirm fires a single
  // POST /transfers/bulk. Falls back to the legacy per-row form for
  // odd-shape moves via a collapsible "Advanced (single-row)" panel.
  const [fromWhId, setFromWhId] = useState('');
  const [toWhId, setToWhId] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  // Selected rows -> { [product_id]: { selected: bool, qty: number, on_hand: number } }
  const [rowState, setRowState] = useState({});
  const [advancedOpen, setAdvancedOpen] = useState(false);
  // Legacy single-row form retained for pallet/location-precise moves.
  const [form, setForm] = useState({ from_placement_id: '', to_warehouse_id: '', to_zone: '', to_location_id: '', to_pallet_id: '', qty: '', notes: '' });

  const loadLog = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/warehouses/transfers`, { headers: authHeaders });
      setLog(r.data.transfers || []);
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setLoading(false); }
  };
  useEffect(() => { loadLog(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const patch = (u) => setForm((p) => ({ ...p, ...u }));

  // ---- Bulk transfer: derive on-hand per product for the source WH -----
  const sourceStockByProduct = useMemo(() => {
    if (!fromWhId) return [];
    const map = new Map();
    for (const p of (placements || [])) {
      if (p.warehouse_id !== fromWhId) continue;
      const free = Math.max(0, (Number(p.qty) || 0) - (Number(p.allocated_qty) || 0));
      if (free <= 0) continue;
      const key = p.product_id;
      if (!map.has(key)) {
        const prod = productsById[key] || {};
        map.set(key, {
          product_id: key,
          product_name: prod.name || p.product_name || key,
          category_slug: prod.category_slug || '(uncategorized)',
          category_name: prod.category_name || prod.category_slug || 'Uncategorized',
          on_hand: 0,
          allocated: 0,
        });
      }
      const bucket = map.get(key);
      bucket.on_hand += free;
      bucket.allocated += (Number(p.allocated_qty) || 0);
    }
    // Stable sort: category name then product name.
    return Array.from(map.values()).sort((a, b) => (
      (a.category_name || '').localeCompare(b.category_name || '')
      || (a.product_name || '').localeCompare(b.product_name || '')
    ));
  }, [placements, fromWhId, productsById]);

  const byCategory = useMemo(() => {
    const groups = new Map();
    for (const row of sourceStockByProduct) {
      const k = row.category_name || 'Uncategorized';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(row);
    }
    return Array.from(groups.entries()).map(([name, rows]) => ({ name, rows }));
  }, [sourceStockByProduct]);

  // Reset row-state when the From changes (avoid stale selections
  // that reference products absent from the new source).
  useEffect(() => {
    setRowState({});
  }, [fromWhId]);

  const setRow = (pid, patchObj) => setRowState((prev) => ({
    ...prev,
    [pid]: { ...(prev[pid] || {}), ...patchObj },
  }));

  const selectAll = (checked) => {
    const next = {};
    for (const r of sourceStockByProduct) {
      next[r.product_id] = {
        selected: !!checked,
        qty: (rowState[r.product_id]?.qty) ?? '',
        on_hand: r.on_hand,
      };
    }
    setRowState(next);
  };

  const selectCategory = (rows, checked) => {
    const next = { ...rowState };
    for (const r of rows) {
      next[r.product_id] = {
        selected: !!checked,
        qty: (next[r.product_id]?.qty) ?? '',
        on_hand: r.on_hand,
      };
    }
    setRowState(next);
  };

  const fillFullQuantities = () => {
    const next = { ...rowState };
    for (const r of sourceStockByProduct) {
      if (next[r.product_id]?.selected) {
        next[r.product_id] = { ...next[r.product_id], qty: String(r.on_hand) };
      }
    }
    setRowState(next);
  };

  // Feb 2026 — Category quick-action.
  // Selecting a category from the "Transfer by category" dropdown
  // atomically (a) clears any previous selection, (b) selects every
  // row in that category currently sitting in the From warehouse,
  // and (c) fills each row's transfer qty to its full on-hand value.
  // The user can then click the existing "Confirm bulk transfer"
  // button, or trigger `transferCategoryNow(name)` for a
  // fire-immediately shortcut.
  const applyCategorySelection = (categoryName) => {
    const group = byCategory.find((g) => g.name === categoryName);
    if (!group) return [];
    const next = {};
    for (const r of group.rows) {
      next[r.product_id] = {
        selected: true,
        qty: String(r.on_hand),
        on_hand: r.on_hand,
      };
    }
    setRowState(next);
    return group.rows;
  };

  const transferCategoryNow = async (categoryName) => {
    if (!fromWhId || !toWhId) {
      toast({ title: 'Pick both warehouses', description: 'Select From and To before choosing a category.' });
      return;
    }
    if (fromWhId === toWhId) {
      toast({ title: 'Same warehouse', description: 'Source and destination must differ.' });
      return;
    }
    const rows = applyCategorySelection(categoryName);
    if (!rows.length) {
      toast({ title: 'Nothing to transfer', description: `No stock in "${categoryName}" for this source warehouse.` });
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/warehouses/transfers/bulk`,
        {
          from_warehouse_id: fromWhId,
          to_warehouse_id: toWhId,
          items: rows.map((row) => ({ product_id: row.product_id, qty: row.on_hand })),
          notes: `Category transfer: ${categoryName}${bulkNotes ? ` — ${bulkNotes}` : ''}`,
        },
        { headers: authHeaders },
      );
      const t = r.data?.totals || {};
      const pending = !!r.data?.pending_approval;
      toast({
        title: pending
          ? `Category "${categoryName}" queued for approval`
          : `Category "${categoryName}" transferred`,
        description: `${t.items_moved || 0}/${t.items_requested || 0} items · ${t.qty_moved || 0}/${t.qty_requested || 0} qty`,
      });
      if (Array.isArray(r.data?.warnings) && r.data.warnings.length) {
        toast({
          title: `${r.data.warnings.length} shortfall${r.data.warnings.length === 1 ? '' : 's'}`,
          description: r.data.warnings.join(' · '),
        });
      }
      setRowState({});
      await loadLog();
      onReload();
    } catch (e) {
      toast({ title: 'Category transfer failed', description: e.response?.data?.detail || 'Try again.' });
    } finally {
      setBusy(false);
    }
  };

  const selectedCount = useMemo(
    () => sourceStockByProduct.filter((r) => rowState[r.product_id]?.selected).length,
    [sourceStockByProduct, rowState],
  );
  const allSelected = selectedCount > 0 && selectedCount === sourceStockByProduct.length;
  const anySelected = selectedCount > 0;

  const runBulkTransfer = async () => {
    if (!fromWhId || !toWhId) {
      toast({ title: 'Pick both warehouses', description: 'Select From and To before confirming.' });
      return;
    }
    if (fromWhId === toWhId) {
      toast({ title: 'Same warehouse', description: 'Source and destination must differ.' });
      return;
    }
    const items = sourceStockByProduct
      .filter((r) => rowState[r.product_id]?.selected && Number(rowState[r.product_id]?.qty) > 0)
      .map((r) => ({
        product_id: r.product_id,
        qty: Math.min(Number(rowState[r.product_id].qty) || 0, r.on_hand),
      }));
    if (!items.length) {
      toast({ title: 'Nothing to transfer', description: 'Select at least one item with a qty > 0.' });
      return;
    }
    const shortLines = items.filter((i) => {
      const src = sourceStockByProduct.find((s) => s.product_id === i.product_id);
      return src && i.qty > src.on_hand;
    });
    if (shortLines.length) {
      toast({ title: 'Qty exceeds on-hand', description: 'Reduce the quantity on flagged lines and try again.' });
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/warehouses/transfers/bulk`,
        {
          from_warehouse_id: fromWhId,
          to_warehouse_id: toWhId,
          items,
          notes: bulkNotes,
        },
        { headers: authHeaders },
      );
      const t = r.data?.totals || {};
      const pending = !!r.data?.pending_approval;
      toast({
        title: pending ? 'Bulk transfer queued for approval' : 'Bulk transfer complete',
        description: `${t.items_moved || 0}/${t.items_requested || 0} items · ${t.qty_moved || 0}/${t.qty_requested || 0} qty`,
      });
      if (Array.isArray(r.data?.warnings) && r.data.warnings.length) {
        toast({
          title: `${r.data.warnings.length} shortfall${r.data.warnings.length === 1 ? '' : 's'}`,
          description: r.data.warnings.join(' · '),
        });
      }
      setRowState({});
      setBulkNotes('');
      await loadLog();
      onReload();
    } catch (e) {
      toast({ title: 'Bulk transfer failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setBusy(false); }
  };

  const create = async () => {
    if (!form.from_placement_id || !form.to_warehouse_id || !form.qty) {
      toast({ title: 'Missing fields', description: 'Source placement, destination warehouse and qty are required.' });
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/warehouses/transfers`, { ...form, qty: Number(form.qty) || 0 }, { headers: authHeaders });
      const status = r.data?.status || 'approved';
      toast({
        title: status === 'pending_approval' ? 'Transfer sent for approval' : 'Transfer complete',
        description: status === 'pending_approval' ? 'A Warehouse Manager must approve it before stock moves.' : undefined,
      });
      setForm({ from_placement_id: '', to_warehouse_id: '', to_zone: '', to_location_id: '', to_pallet_id: '', qty: '', notes: '' });
      await loadLog();
      onReload();
    } catch (e) {
      toast({ title: 'Transfer failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setBusy(false); }
  };

  const approveTransfer = async (id) => {
    try {
      await axios.put(`${API}/admin/warehouses/transfers/${id}/approve`, {}, { headers: authHeaders });
      toast({ title: 'Transfer approved — stock moved.' });
      await loadLog();
      onReload();
    } catch (e) {
      toast({ title: 'Approve failed', description: e.response?.data?.detail || 'Try again.' });
    }
  };

  const rejectTransfer = async (id) => {
    if (!window.confirm('Reject this pending transfer? No stock will move.')) return;
    try {
      await axios.put(`${API}/admin/warehouses/transfers/${id}/reject`, {}, { headers: authHeaders });
      toast({ title: 'Transfer rejected' });
      await loadLog();
    } catch (e) {
      toast({ title: 'Reject failed', description: e.response?.data?.detail || 'Try again.' });
    }
  };

  return (
    <div>
      {/* ==== BULK TRANSFER (redesigned Feb 2026) =================== */}
      <div
        className="rounded-2xl border border-neutral-200 bg-white p-4 mb-4"
        data-testid="admin-bulk-transfer"
      >
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-neutral-500">Bulk transfer</div>
            <div className="text-lg font-extrabold text-neutral-900">Move stock in one batch</div>
          </div>
          <div className="text-[11px] text-neutral-500">
            FEFO order · allocated stock never moves · one click to fill full qty
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-600 block mb-1">
              From warehouse <span className="text-rose-600">*</span>
            </span>
            <select
              value={fromWhId}
              onChange={(e) => setFromWhId(e.target.value)}
              disabled={busy}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
              data-testid="admin-bulk-from-warehouse"
            >
              <option value="">Select source…</option>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>{w.name}{w.is_default ? ' (Default)' : ''}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-600 block mb-1">
              To warehouse <span className="text-rose-600">*</span>
            </span>
            <select
              value={toWhId}
              onChange={(e) => setToWhId(e.target.value)}
              disabled={busy}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
              data-testid="admin-bulk-to-warehouse"
            >
              <option value="">Select destination…</option>
              {warehouses
                .filter((w) => w.id !== fromWhId)
                .map((w) => (
                  <option key={w.id} value={w.id}>{w.name}{w.is_default ? ' (Default)' : ''}</option>
                ))}
            </select>
          </label>
        </div>

        {!fromWhId && (
          <div className="mt-4 rounded-md border border-dashed border-neutral-300 bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500" data-testid="admin-bulk-empty-hint">
            Select a source warehouse to load its on-hand stock.
          </div>
        )}

        {fromWhId && sourceStockByProduct.length === 0 && (
          <div className="mt-4 rounded-md border border-neutral-200 bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500" data-testid="admin-bulk-no-stock">
            No free stock in <b>{warehousesById[fromWhId]?.name || fromWhId}</b> to transfer.
          </div>
        )}

        {fromWhId && sourceStockByProduct.length > 0 && (
          <div className="mt-4">
            {/* Feb 2026 — Transfer by category quick-action.
                Picking a category from the dropdown auto-selects every
                product in that category present in the From warehouse
                and fills each row's qty to its on-hand total. The
                sibling "Transfer this category" button fires the bulk
                POST in one click. Users who want to review the auto-
                filled rows can leave the button alone and hit the main
                "Confirm bulk transfer" button below the table. */}
            <div
              className="mb-3 rounded-lg border border-sky-200 bg-sky-50/60 px-3 py-2 flex flex-wrap items-center gap-2"
              data-testid="admin-bulk-category-quickaction"
            >
              <div className="text-[10px] uppercase tracking-widest font-bold text-sky-800 mr-1">
                Transfer by category
              </div>
              <select
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  applyCategorySelection(v);
                  e.target.value = ''; // reset so choosing the same cat again re-selects
                }}
                disabled={busy}
                className="flex-1 min-w-[10rem] rounded-md border border-sky-300 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                data-testid="admin-bulk-category-select"
              >
                <option value="">Choose a category to auto-select…</option>
                {byCategory.map((g) => (
                  <option key={g.name} value={g.name}>
                    {g.name} — {g.rows.length} item{g.rows.length === 1 ? '' : 's'} · {g.rows.reduce((s, r) => s + r.on_hand, 0)} on hand
                  </option>
                ))}
              </select>
              <select
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  transferCategoryNow(v);
                  e.target.value = '';
                }}
                disabled={busy || !toWhId}
                className="rounded-md border border-emerald-400 bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1.5 text-xs font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="admin-bulk-category-transfer-now"
                title={!toWhId ? 'Pick a destination warehouse first' : 'Transfer every item in the chosen category to the destination'}
              >
                <option value="">Transfer entire category now…</option>
                {byCategory.map((g) => (
                  <option key={g.name} value={g.name}>{g.name}</option>
                ))}
              </select>
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 py-2 border-b border-neutral-200">
              <label className="inline-flex items-center gap-1.5 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => selectAll(e.target.checked)}
                  className="w-4 h-4 accent-[#0078D4]"
                  data-testid="admin-bulk-select-all"
                />
                <span className="font-semibold">Select all</span>
                <span className="text-neutral-500 text-xs">({selectedCount}/{sourceStockByProduct.length})</span>
              </label>
              <button
                type="button"
                onClick={fillFullQuantities}
                disabled={!anySelected || busy}
                className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-3 py-1 text-xs font-bold disabled:opacity-50"
                data-testid="admin-bulk-fill-full-qty"
              >
                Transfer full quantities
              </button>
            </div>

            {/* Category groups */}
            <div className="mt-2 divide-y divide-neutral-100">
              {byCategory.map(({ name, rows }) => {
                const selectedInCat = rows.filter((r) => rowState[r.product_id]?.selected).length;
                const catAllOn = selectedInCat > 0 && selectedInCat === rows.length;
                return (
                  <div key={name} className="py-2" data-testid={`admin-bulk-category-${name}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <button
                        type="button"
                        onClick={() => selectCategory(rows, !catAllOn)}
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${catAllOn ? 'bg-[#0078D4] border-[#0078D4] text-white' : 'bg-white border-neutral-300 text-neutral-700 hover:border-[#0078D4]'}`}
                        data-testid={`admin-bulk-select-category-${name}`}
                      >
                        {catAllOn ? 'Deselect all' : `Select all in ${name}`}
                      </button>
                      <span className="text-[11px] text-neutral-500">
                        {selectedInCat}/{rows.length} selected · {rows.reduce((sum, r) => sum + r.on_hand, 0)} on hand
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-400">
                            <th className="pl-0 pr-2 py-1 w-8"> </th>
                            <th className="px-2 py-1">Product</th>
                            <th className="px-2 py-1 text-right w-24">On hand</th>
                            <th className="px-2 py-1 text-right w-32">Transfer qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => {
                            const st = rowState[r.product_id] || {};
                            const selected = !!st.selected;
                            const qtyNum = Number(st.qty) || 0;
                            const over = selected && qtyNum > r.on_hand;
                            return (
                              <tr
                                key={r.product_id}
                                className={`border-t border-neutral-100 ${selected ? 'bg-sky-50/40' : ''}`}
                                data-testid={`admin-bulk-row-${r.product_id}`}
                              >
                                <td className="pl-0 pr-2 py-1.5">
                                  <input
                                    type="checkbox"
                                    checked={selected}
                                    onChange={(e) => setRow(r.product_id, { selected: e.target.checked, on_hand: r.on_hand })}
                                    className="w-4 h-4 accent-[#0078D4]"
                                    data-testid={`admin-bulk-check-${r.product_id}`}
                                  />
                                </td>
                                <td className="px-2 py-1.5">
                                  <div className="font-medium text-neutral-900 truncate">{r.product_name}</div>
                                  <div className="text-[10px] text-neutral-500">{r.category_name}</div>
                                </td>
                                <td className="px-2 py-1.5 text-right tabular-nums font-semibold">{r.on_hand}</td>
                                <td className="px-2 py-1.5 text-right">
                                  <input
                                    type="number"
                                    min="0"
                                    max={r.on_hand}
                                    value={st.qty ?? ''}
                                    onChange={(e) => setRow(r.product_id, { qty: e.target.value, on_hand: r.on_hand, selected: true })}
                                    disabled={!selected}
                                    className={`w-24 rounded-md border px-2 py-1 text-sm text-right focus:outline-none focus:ring-2 disabled:bg-neutral-100 disabled:cursor-not-allowed ${over ? 'border-rose-400 focus:ring-rose-400' : 'border-neutral-300 focus:ring-[#0078D4]'}`}
                                    data-testid={`admin-bulk-qty-${r.product_id}`}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Notes + confirm */}
            <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-end gap-2">
              <label className="flex-1 block">
                <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-600 block mb-1">Notes (optional)</span>
                <Input
                  value={bulkNotes}
                  onChange={(e) => setBulkNotes(e.target.value)}
                  placeholder="e.g. Restock TP branch before weekend"
                  disabled={busy}
                  className="text-sm"
                  data-testid="admin-bulk-notes"
                />
              </label>
              <Button
                onClick={runBulkTransfer}
                disabled={busy || !fromWhId || !toWhId || !anySelected}
                className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full disabled:opacity-60 sm:min-w-[220px]"
                data-testid="admin-bulk-confirm"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Confirm bulk transfer${anySelected ? ` (${selectedCount})` : ''}`}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* ==== ADVANCED (legacy single-row move) ===================== */}
      <div className="rounded-2xl border border-neutral-200 bg-white p-4 mb-4" data-testid="admin-transfer-form">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-neutral-500 font-semibold hover:text-neutral-800"
          data-testid="admin-transfer-advanced-toggle"
        >
          <span>Advanced — single-row transfer (choose pallet / dest coordinates)</span>
          <span>{advancedOpen ? '−' : '+'}</span>
        </button>
        {advancedOpen && (
          <div className="mt-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              <select value={form.from_placement_id} onChange={(e) => patch({ from_placement_id: e.target.value })} className="text-sm rounded-md border border-neutral-300 px-3 py-2" data-testid="admin-transfer-source">
                <option value="">Source placement…</option>
                {placements.map((p) => (
                  <option key={p.id} value={p.id}>
                    {productsById[p.product_id]?.name || p.product_name || p.product_id} · batch {formatDate(p.production_date)} · qty {p.qty} @ {warehousesById[p.warehouse_id]?.name}/{p.pallet_id || '—'}
                  </option>
                ))}
              </select>
              <select value={form.to_warehouse_id} onChange={(e) => patch({ to_warehouse_id: e.target.value })} className="text-sm rounded-md border border-neutral-300 px-3 py-2" data-testid="admin-transfer-dest-warehouse">
                <option value="">Destination warehouse…</option>
                {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
              <Input type="number" value={form.qty} onChange={(e) => patch({ qty: e.target.value })} placeholder="Qty" className="text-sm" data-testid="admin-transfer-qty" />
              <Input value={form.to_zone} onChange={(e) => patch({ to_zone: e.target.value })} placeholder="Dest zone" className="text-sm" data-testid="admin-transfer-dest-zone" />
              <Input value={form.to_location_id} onChange={(e) => patch({ to_location_id: e.target.value })} placeholder="Dest location" className="text-sm" data-testid="admin-transfer-dest-loc" />
              <Input value={form.to_pallet_id} onChange={(e) => patch({ to_pallet_id: e.target.value })} placeholder="Dest pallet" className="text-sm" data-testid="admin-transfer-dest-pallet" />
            </div>
            <Input value={form.notes} onChange={(e) => patch({ notes: e.target.value })} placeholder="Notes (optional)" className="text-sm mt-2" data-testid="admin-transfer-notes" />
            <div className="mt-2 flex items-center gap-3">
              <Button onClick={create} disabled={busy} className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full" data-testid="admin-transfer-create-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Transfer</>}
              </Button>
              <a href={`${API}/admin/warehouses/pallets/barcode-sheet`} target="_blank" rel="noreferrer" className="text-sm text-[#0078D4] hover:underline" data-testid="admin-barcode-sheet-link">
                Open barcode sticker sheet →
              </a>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-transfer-log">
          <thead className="bg-neutral-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Who</th>
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">Batch</th>
              <th className="px-3 py-2">From</th>
              <th className="px-3 py-2">To</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Notes</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="text-center py-6 text-neutral-400"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…</td></tr>}
            {!loading && log.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-neutral-400">No transfers yet.</td></tr>}
            {!loading && log.map((t) => (
              <tr key={t.id} className="border-t border-neutral-100" data-testid={`admin-transfer-row-${t.id}`}>
                <td className="px-3 py-2 font-mono text-[11px]">{formatDateTime(t.initiated_at)}</td>
                <td className="px-3 py-2 text-xs">{t.initiated_by}</td>
                <td className="px-3 py-2">{productsById[t.product_id]?.name || t.product_id}</td>
                <td className="px-3 py-2 font-mono text-xs">{formatDate(t.production_date)}</td>
                <td className="px-3 py-2">{warehousesById[t.from_warehouse_id]?.name || t.from_warehouse_id}<span className="text-neutral-500 text-[10px]"> / {t.from_zone || '—'}/{t.from_location_id || '—'}/{t.from_pallet_id || '—'}</span></td>
                <td className="px-3 py-2">{warehousesById[t.to_warehouse_id]?.name || t.to_warehouse_id}<span className="text-neutral-500 text-[10px]"> / {t.to_zone || '—'}/{t.to_location_id || '—'}/{t.to_pallet_id || '—'}</span></td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">{t.qty}</td>
                <td className="px-3 py-2 text-xs">
                  {t.status === 'pending_approval' ? (
                    <div className="flex items-center gap-1.5">
                      <span className="inline-block bg-amber-100 text-amber-800 rounded-full px-2 py-0.5 text-[10px] font-bold">PENDING</span>
                      <button
                        type="button"
                        onClick={() => approveTransfer(t.id)}
                        className="text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-0.5 text-[10px] font-bold border border-emerald-300 rounded-full px-1.5 py-0.5"
                        data-testid={`admin-transfer-approve-${t.id}`}
                      ><Check className="w-3 h-3" /> Approve</button>
                      <button
                        type="button"
                        onClick={() => rejectTransfer(t.id)}
                        className="text-red-700 hover:text-red-900 inline-flex items-center gap-0.5 text-[10px] font-bold border border-red-300 rounded-full px-1.5 py-0.5"
                        data-testid={`admin-transfer-reject-${t.id}`}
                      ><XIcon className="w-3 h-3" /> Reject</button>
                    </div>
                  ) : t.status === 'rejected' ? (
                    <span className="inline-block bg-red-100 text-red-800 rounded-full px-2 py-0.5 text-[10px] font-bold">REJECTED</span>
                  ) : (
                    <span className="inline-block bg-emerald-100 text-emerald-800 rounded-full px-2 py-0.5 text-[10px] font-bold">APPROVED</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-neutral-500">{t.notes || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============================================================
// ALERTS TAB (July 2026 phase-2)
// ============================================================
const AlertsTab = ({ authHeaders }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/warehouses/replenishment-alerts`, { headers: authHeaders });
      setAlerts(r.data.alerts || []);
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-neutral-600">
          Alerts fire the moment a warehouse&apos;s on-hand qty for a product drops below the configured minimum. Suggested sources rank by richest available qty first. To configure thresholds, use the API <code className="text-xs bg-neutral-100 px-1 rounded">PUT /admin/warehouses/{'{warehouse_id}'}/min-qty/{'{product_id}'}</code>.
        </p>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="rounded-full" data-testid="admin-alerts-refresh">Refresh</Button>
      </div>
      <div className="rounded-2xl border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-alerts-table">
          <thead className="bg-neutral-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <th className="px-3 py-2">Warehouse</th>
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2 text-right">Current</th>
              <th className="px-3 py-2 text-right">Min</th>
              <th className="px-3 py-2 text-right">Deficit</th>
              <th className="px-3 py-2">Suggested sources</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} className="text-center py-6 text-neutral-400"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…</td></tr>}
            {!loading && alerts.length === 0 && <tr><td colSpan={6} className="text-center py-6 text-neutral-400">No alerts. All monitored products are above their minimum thresholds.</td></tr>}
            {!loading && alerts.map((a) => (
              <tr key={`${a.warehouse_id}:${a.product_id}`} className="border-t border-neutral-100 bg-red-50/40" data-testid={`admin-alert-row-${a.warehouse_id}-${a.product_id}`}>
                <td className="px-3 py-2 font-medium">{a.warehouse_name}</td>
                <td className="px-3 py-2">{a.product_name}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums text-red-700">{a.current_qty}</td>
                <td className="px-3 py-2 text-right tabular-nums">{a.min_qty}</td>
                <td className="px-3 py-2 text-right tabular-nums font-semibold text-red-700">{a.deficit}</td>
                <td className="px-3 py-2 text-xs">
                  {a.suggested_sources.length === 0
                    ? <span className="text-neutral-400">No source has enough stock — chase replenishment externally.</span>
                    : a.suggested_sources.map((s) => (
                      <span key={s.warehouse_id} className="inline-block mr-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5">
                        {s.warehouse_name} ({s.available_qty})
                      </span>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ============================================================
// AUDIT LOG TAB (July 2026 — RBAC)
// ============================================================
const AuditLogTab = ({ authHeaders, warehousesById }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ warehouse_id: '', action: '', email_filter: '' });
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.warehouse_id) params.warehouse_id = filters.warehouse_id;
      if (filters.action) params.action = filters.action;
      if (filters.email_filter) params.email_filter = filters.email_filter;
      const r = await axios.get(`${API}/admin/warehouses/audit-log`, { headers: authHeaders, params });
      setRows(r.data.rows || []);
    } catch (e) {
      toast({ title: 'Audit log unavailable', description: e.response?.data?.detail || 'Warehouse Manager role required.' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const patch = (u) => setFilters((f) => ({ ...f, ...u }));

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select value={filters.warehouse_id} onChange={(e) => patch({ warehouse_id: e.target.value })} className="text-sm rounded-md border border-neutral-300 px-2 py-1.5" data-testid="admin-audit-filter-warehouse">
          <option value="">All warehouses</option>
          {Object.values(warehousesById || {}).map((w) => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
        <Input placeholder="Filter by email…" value={filters.email_filter} onChange={(e) => patch({ email_filter: e.target.value })} className="text-sm max-w-xs" data-testid="admin-audit-filter-email" />
        <Input placeholder="action code (e.g. transfer.approve)" value={filters.action} onChange={(e) => patch({ action: e.target.value })} className="text-sm max-w-xs font-mono" data-testid="admin-audit-filter-action" />
        <Button size="sm" onClick={load} disabled={loading} className="rounded-full bg-[#0F172A] text-white" data-testid="admin-audit-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Filter'}
        </Button>
        <span className="text-xs text-neutral-500 ml-auto">{rows.length} row(s)</span>
      </div>
      <div className="rounded-2xl border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-audit-table">
          <thead className="bg-neutral-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2">Who</th>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">Action</th>
              <th className="px-3 py-2">Warehouse</th>
              <th className="px-3 py-2">Entity</th>
              <th className="px-3 py-2">Details</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="text-center py-6 text-neutral-400"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-neutral-400">No matching audit rows.</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-neutral-100" data-testid={`admin-audit-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-[11px] whitespace-nowrap">{formatDateTime(r.timestamp)}</td>
                <td className="px-3 py-2 text-xs">{r.email}</td>
                <td className="px-3 py-2 text-xs">{(r.role || '').replace('_', ' ')}</td>
                <td className="px-3 py-2 font-mono text-xs">{r.action}</td>
                <td className="px-3 py-2 text-xs">{r.warehouse_name || warehousesById?.[r.warehouse_id]?.name || r.warehouse_id || '—'}</td>
                <td className="px-3 py-2 font-mono text-[10px] text-neutral-500">{r.entity_type}:{(r.entity_id || '').slice(0, 8)}</td>
                <td className="px-3 py-2 text-[11px] text-neutral-600">
                  <code className="whitespace-pre-wrap break-all">{Object.keys(r.details || {}).length ? JSON.stringify(r.details) : '—'}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};




// ============================================================
// REQUISITIONS TAB (July 2026)
// ============================================================
// Warehouse staff (Manager / Floor In-charge) raise internal
// requisitions here. Each row shows product name, SKU, available qty
// and unit price only — no images (per user spec). Enter a required
// qty against the rows you need, then hit Submit. The backend creates
// an Order with `order_type="requisition"`; on status → confirmed the
// existing FEFO planner attaches a pick list, exactly like a customer
// order.
const RequisitionsTab = ({ authHeaders }) => {
  const [rows, setRows] = useState([]);
  const [qty, setQty] = useState({}); // { product_id: number }
  const [requestedBy, setRequestedBy] = useState('');
  const [notes, setNotes] = useState('');
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lastCreated, setLastCreated] = useState(null); // {order_number, id}
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/warehouses/requisitions/stock-list`, { headers: authHeaders });
      setRows(r.data.products || []);
    } catch (e) {
      toast({ title: 'Failed to load stock list', description: e.response?.data?.detail || 'Try again.' });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.sku || '').toLowerCase().includes(q),
    );
  }, [rows, filter]);

  const selectedItems = useMemo(() => {
    return Object.entries(qty)
      .map(([pid, n]) => ({ pid, qty: Number(n) || 0 }))
      .filter((x) => x.qty > 0);
  }, [qty]);

  const cartTotal = useMemo(() => {
    return selectedItems.reduce((sum, it) => {
      const r = rows.find((x) => x.product_id === it.pid);
      return sum + (r ? Number(r.unit_price || 0) * it.qty : 0);
    }, 0);
  }, [selectedItems, rows]);

  const submit = async () => {
    if (!requestedBy.trim()) {
      toast({ title: 'Requested-by is required', description: 'Enter the warehouse staff name raising this requisition.' });
      return;
    }
    if (selectedItems.length === 0) {
      toast({ title: 'No items selected', description: 'Enter a qty against at least one product.' });
      return;
    }
    setBusy(true);
    try {
      const body = {
        requested_by: requestedBy.trim(),
        notes: notes.trim(),
        items: selectedItems.map((x) => ({ product_id: x.pid, qty: x.qty })),
      };
      const r = await axios.post(`${API}/admin/warehouses/requisitions`, body, { headers: authHeaders });
      const created = r.data;
      setLastCreated({ id: created.id, order_number: created.order_number });
      toast({
        title: `Requisition ${created.order_number} created`,
        description: 'Set status → Confirmed on the Orders page to auto-generate the pick list.',
      });
      setQty({}); setNotes('');
      // Keep requestedBy filled so the same person can raise multiple.
    } catch (e) {
      toast({ title: 'Submit failed', description: e.response?.data?.detail || 'Try again.' });
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="mb-4 rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2 font-semibold">New requisition</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          <Input
            value={requestedBy}
            onChange={(e) => setRequestedBy(e.target.value)}
            placeholder="Requested by (warehouse staff name)"
            className="text-sm"
            data-testid="requisition-requested-by"
          />
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Notes (optional)"
            className="text-sm"
            data-testid="requisition-notes"
          />
        </div>
        <div className="flex items-center gap-2 mb-2">
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by product name or SKU…"
            className="text-sm max-w-sm"
            data-testid="requisition-search"
          />
          <span className="text-xs text-neutral-500 ml-auto" data-testid="requisition-selected-count">
            {selectedItems.length} item(s) selected · <Rial /> {cartTotal.toFixed(3)}
          </span>
          <Button
            onClick={submit}
            disabled={busy || selectedItems.length === 0}
            className="rounded-full bg-[#0F172A] hover:bg-black text-white disabled:opacity-50"
            data-testid="requisition-submit-btn"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><ClipboardEdit className="w-4 h-4 mr-1" /> Submit requisition</>}
          </Button>
        </div>
        {lastCreated && (
          <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-1.5" data-testid="requisition-last-created">
            Requisition <strong>{lastCreated.order_number}</strong> created. Open{' '}
            <a href="/admin/orders" className="underline font-semibold">Orders</a>{' '}
            → set status to <strong>Confirmed</strong> to generate the pick list.
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-neutral-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="requisition-stock-table">
          <thead className="bg-neutral-50">
            <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <th className="px-3 py-2">Product</th>
              <th className="px-3 py-2">SKU</th>
              <th className="px-3 py-2 text-right">Available qty</th>
              <th className="px-3 py-2 text-right">Unit price (<Rial />)</th>
              <th className="px-3 py-2 text-right w-32">Required qty</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="text-center py-6 text-neutral-400"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-neutral-400">No products match your filter.</td></tr>}
            {!loading && filtered.map((r) => {
              const current = qty[r.product_id] || '';
              const isSelected = Number(current) > 0;
              return (
                <tr key={r.product_id} className={`border-t border-neutral-100 ${isSelected ? 'bg-[#F4FAFF]' : ''}`} data-testid={`requisition-row-${r.product_id}`}>
                  <td className="px-3 py-2 font-medium text-[#0F172A]">{r.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-neutral-500">{r.sku || '—'}</td>
                  <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.available_qty === 0 ? 'text-red-600' : 'text-emerald-700'}`}>
                    {r.available_qty} <span className="text-neutral-400 font-normal text-[10px]">{r.uom}</span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{Number(r.unit_price || 0).toFixed(3)}</td>
                  <td className="px-3 py-2 text-right">
                    <Input
                      type="number"
                      min="0"
                      value={current}
                      onChange={(e) => {
                        const v = e.target.value;
                        setQty((prev) => {
                          const next = { ...prev };
                          if (!v || Number(v) <= 0) delete next[r.product_id];
                          else next[r.product_id] = Number(v);
                          return next;
                        });
                      }}
                      className="text-sm text-right w-24 ml-auto"
                      placeholder="0"
                      data-testid={`requisition-qty-${r.product_id}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};



// -----------------------------------------------------------------------------
// LoadingTab — orders that finished picking and are waiting for the
// warehouse manager to press "Start Loading". Fires the manual timeline
// step on the public tracker.
// -----------------------------------------------------------------------------
const LoadingTab = ({ authHeaders, me }) => {
  const isManager = !!(me && (me.is_super_admin || me.role === 'manager' || me.role === 'floor_incharge'));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [erpPromptOrder, setErpPromptOrder] = useState(null); // {id, order_number}
  const [erpValue, setErpValue] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/warehouse/ready-to-load`, { headers: authHeaders });
      setRows(data?.orders || []);
    } catch (err) {
      toast({ title: 'Could not load orders', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openErpPrompt = (order) => {
    if (!isManager) return;
    setErpPromptOrder(order);
    setErpValue('');
    setScanOpen(false);
  };

  const cancelErpPrompt = () => {
    setErpPromptOrder(null);
    setErpValue('');
    setScanOpen(false);
  };

  const confirmStartLoading = async () => {
    if (!erpPromptOrder) return;
    const erp = (erpValue || '').trim();
    if (!erp) {
      toast({ title: 'ERP Invoice # required', description: 'Scan the barcode or type the invoice number to continue.' });
      return;
    }
    setBusyId(erpPromptOrder.id);
    try {
      await axios.post(
        `${API}/admin/orders/${erpPromptOrder.id}/start-loading`,
        { erp_invoice_number: erp },
        { headers: authHeaders },
      );
      toast({ title: 'Loading started', description: `Order ${erpPromptOrder.order_number} · ERP #${erp}` });
      setRows((prev) => prev.filter((r) => r.id !== erpPromptOrder.id));
      cancelErpPrompt();
    } catch (err) {
      toast({ title: 'Could not start loading', description: err?.response?.data?.detail || 'Please try again.' });
    } finally {
      setBusyId('');
    }
  };

  return (
    <div data-testid="loading-tab-root">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-bold text-[#0F172A]">Ready to load</h3>
          <p className="text-xs text-neutral-500">Orders that finished picking and are waiting for loading to start.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="loading-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="py-16 flex justify-center text-neutral-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center text-sm text-neutral-500 italic" data-testid="loading-empty">
          No orders are ready to load. Orders appear here once picking is complete.
        </div>
      ) : (
        <div className="overflow-x-auto border border-neutral-200 rounded-lg">
          <table className="w-full text-sm" data-testid="loading-table">
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2">Order</th>
                <th className="text-left px-3 py-2">Customer</th>
                <th className="text-right px-3 py-2">Items</th>
                <th className="text-right px-3 py-2">Total</th>
                <th className="text-left px-3 py-2">Picking finished</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id} className="border-t border-neutral-100" data-testid={`ready-to-load-row-${o.order_number}`}>
                  <td className="px-3 py-2 font-mono text-xs text-[#0F172A]">{o.order_number}</td>
                  <td className="px-3 py-2 text-[#0F172A]" data-testid={`ready-to-load-party-${o.order_number}`}>
                    {o.customer_business_name || o.customer_name || '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{o.item_count}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{Number(o.total || 0).toFixed(3)}</td>
                  <td className="px-3 py-2 text-xs text-neutral-600">{o.picking_completed_at ? formatDateTime(o.picking_completed_at) : '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      onClick={() => openErpPrompt(o)}
                      disabled={!isManager || busyId === o.id}
                      data-testid={`start-loading-btn-${o.order_number}`}
                      className="bg-[#0078D4] hover:bg-[#005A9E] text-white"
                    >
                      {busyId === o.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Boxes className="w-4 h-4 mr-1" />}
                      Start Loading
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!isManager && (
            <div className="text-[11px] text-neutral-500 px-3 py-2 border-t border-neutral-100 bg-neutral-50">
              Only a warehouse manager or floor in-charge can start loading.
            </div>
          )}
        </div>
      )}

      {/* Feb 2026 — ERP Invoice # prompt. Same scan-first behaviour as
          the picking scan modal: barcode/QR-code scan populates the
          field, or the operator types it manually. */}
      {erpPromptOrder && (
        <div
          className="fixed inset-0 z-[130] bg-black/70 flex items-center justify-center p-4"
          onClick={cancelErpPrompt}
          data-testid="erp-invoice-prompt"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base font-bold text-[#0F172A]">ERP Invoice #</h3>
                <p className="text-xs text-neutral-500 mt-1">
                  Scan the invoice barcode or type it manually before loading starts for <span className="font-mono font-semibold">{erpPromptOrder.order_number}</span>.
                </p>
              </div>
              <button
                type="button"
                onClick={cancelErpPrompt}
                className="text-neutral-500 hover:text-neutral-800 p-1"
                data-testid="erp-invoice-cancel-btn"
              >
                <XIcon className="w-4 h-4" />
              </button>
            </div>
            <div className="flex items-stretch gap-2">
              <input
                autoFocus
                type="text"
                value={erpValue}
                onChange={(e) => setErpValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') confirmStartLoading(); }}
                placeholder="Scan or type ERP Invoice #"
                className="flex-1 border border-neutral-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
                data-testid="erp-invoice-input"
              />
              <button
                type="button"
                onClick={() => setScanOpen(true)}
                className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-neutral-300 text-sm font-semibold text-[#0078D4] hover:bg-neutral-50"
                data-testid="erp-invoice-scan-btn"
              >
                <ScanLine className="w-4 h-4" /> Scan
              </button>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={cancelErpPrompt} data-testid="erp-invoice-dismiss-btn">Cancel</Button>
              <Button
                onClick={confirmStartLoading}
                disabled={!erpValue.trim() || busyId === erpPromptOrder.id}
                className="bg-[#0078D4] hover:bg-[#005A9E] text-white"
                data-testid="erp-invoice-confirm-btn"
              >
                {busyId === erpPromptOrder.id ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                Start Loading
              </Button>
            </div>
          </div>
          {scanOpen && (
            <BarcodeScannerModal
              onDetected={(code) => { setErpValue(code); setScanOpen(false); }}
              onClose={() => setScanOpen(false)}
            />
          )}
        </div>
      )}
    </div>
  );
};


// -----------------------------------------------------------------------------
// TasksTab — picking + put-away task management (Feb 2026)
// -----------------------------------------------------------------------------
const TasksTab = ({ authHeaders, me }) => {
  const isManager = !!(me && (me.is_super_admin || me.role === 'manager' || me.role === 'floor_incharge'));
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  // Feb 2026 v11 — cache of orders referenced by ANY task in the pod
  // so the "Order" filter dropdown can render meaningful labels
  // ("MR-00000022 - Barka FG - Chips"). Fetched once alongside the
  // first task load; refreshed on demand.
  const [orderOptions, setOrderOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAssignee, setFilterAssignee] = useState('');
  const [filterOrder, setFilterOrder] = useState('');
  const [scanTask, setScanTask] = useState(null); // task object currently open in scanner
  const [globalScan, setGlobalScan] = useState(false); // top-level "Scan any task" mode
  const [globalQuery, setGlobalQuery] = useState('');
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType) params.set('type', filterType);
      if (filterStatus) params.set('status', filterStatus);
      if (isManager && filterAssignee) params.set('assignee', filterAssignee);
      if (filterOrder) params.set('order_id', filterOrder);
      const q = params.toString();
      const r = await axios.get(`${API}/admin/warehouse/tasks${q ? `?${q}` : ''}`, { headers: authHeaders });
      setTasks(r.data.tasks || []);
      if (isManager && users.length === 0) {
        try {
          const u = await axios.get(`${API}/admin/warehouse/tasks/assignable-users`, { headers: authHeaders });
          setUsers(u.data.users || []);
        } catch { /* non-fatal */ }
      }
      // Populate the order-filter dropdown lazily on first load.
      // We reload only when the current cache is stale (empty) so
      // the dropdown stays responsive; a manager rarely needs a
      // second sync per session.
      if (orderOptions.length === 0) {
        try {
          const o = await axios.get(`${API}/admin/orders?limit=500`, { headers: authHeaders });
          // Keep only orders that have at least one associated
          // warehouse task in the current pod; otherwise the
          // dropdown balloons with orders that filter to nothing.
          const orderById = new Map();
          for (const ord of o.data || []) orderById.set(ord.id, ord);
          const taskOrderIds = new Set((r.data.tasks || []).map((t) => t.order_id).filter(Boolean));
          const opts = Array.from(taskOrderIds)
            .map((oid) => orderById.get(oid))
            .filter(Boolean)
            .map((ord) => ({
              id: ord.id,
              order_number: ord.order_number || ord.id.slice(0, 8),
              // Prefer branch_name; fall back to customer_name so
              // non-branch orders still show a meaningful label.
              branch: ord.branch_name || ord.customer_name || '—',
              // Categories come from the enrichment helper — join
              // with " / " when there are multiple (the "Chips" in
              // the spec's example is the single-cat case).
              category: (ord.categories || []).join(' / ') || '—',
            }))
            // Newest first (order_number is roughly monotonic).
            .sort((a, b) => (b.order_number || '').localeCompare(a.order_number || ''));
          setOrderOptions(opts);
        } catch { /* non-fatal — the filter simply stays empty */ }
      }
    } catch (e) {
      toast({ title: 'Failed to load tasks', description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterType, filterStatus, filterAssignee, filterOrder]);

  const patchTask = async (task, body) => {
    try {
      const r = await axios.patch(`${API}/admin/warehouse/tasks/${task.id}`, body, { headers: authHeaders });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? r.data : t)));
    } catch (e) {
      toast({ title: 'Update failed', description: e?.response?.data?.detail || e?.message });
    }
  };

  const removeTask = async (task) => {
    if (!window.confirm(`Delete this ${task.type} task?`)) return;
    try {
      await axios.delete(`${API}/admin/warehouse/tasks/${task.id}`, { headers: authHeaders });
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || e?.message });
    }
  };

  // Feb 2026 v13 — Bulk-assign every task on the currently-filtered
  // order to a single user in one call. Fires against the new
  // `/bulk-assign-by-order` endpoint so we only make one round-trip
  // regardless of how many tasks are on the order.
  const bulkAssignSelectedOrder = async (assigneeEmail) => {
    if (!filterOrder) return;
    try {
      const r = await axios.post(
        `${API}/admin/warehouse/tasks/bulk-assign-by-order`,
        { order_id: filterOrder, assignee_email: assigneeEmail || null },
        { headers: authHeaders },
      );
      // Optimistically patch the local rows so the UI updates
      // without a round-trip re-fetch.
      setTasks((prev) => prev.map((t) => (
        t.order_id === filterOrder
          ? {
              ...t,
              assignee_email: assigneeEmail || null,
              assignee_name: r.data.assignee_name || null,
              assigned_at: assigneeEmail ? new Date().toISOString() : null,
            }
          : t
      )));
      toast({
        title: assigneeEmail ? 'Tasks assigned' : 'Tasks unassigned',
        description: `${r.data.updated} task${r.data.updated === 1 ? '' : 's'} on this order → ${r.data.assignee_name || 'Unassigned'}.`,
      });
    } catch (e) {
      toast({ title: 'Bulk assign failed', description: e?.response?.data?.detail || e?.message });
    }
  };

  const statusPill = (s) => {
    const map = {
      pending: 'bg-neutral-100 text-neutral-700',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-emerald-100 text-emerald-800',
    };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${map[s] || 'bg-neutral-100 text-neutral-700'}`}>{s.replace('_', ' ')}</span>;
  };

  const typePill = (t) => {
    const map = { picking: 'bg-amber-100 text-amber-800', putaway: 'bg-indigo-100 text-indigo-800' };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${map[t] || 'bg-neutral-100 text-neutral-700'}`}>{t}</span>;
  };

  return (
    <div className="bg-white rounded-xl border border-neutral-200 p-4" data-testid="warehouse-tasks-tab">
      {/* Global scan bar — pick any pending task by scanning either the
          pallet sticker (picking) or the item barcode (put-away). */}
      <div className="flex items-center gap-2 flex-wrap mb-3 p-3 rounded-xl bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white">
        <ScanLine className="w-4 h-4 text-emerald-400" />
        <div className="font-semibold text-sm flex-1">
          Scan a pallet or item barcode to open the matching task
        </div>
        <input
          type="text"
          value={globalQuery}
          onChange={(e) => setGlobalQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && globalQuery.trim()) {
              const q = globalQuery.trim().toUpperCase();
              const hit = tasks.find((t) => (
                (t.pallet_id || '').toUpperCase() === q ||
                (t.location_id || '').toUpperCase() === q ||
                (t.product_id || '').toUpperCase() === q
              ) && t.status !== 'completed');
              if (hit) {
                setScanTask(hit);
                setGlobalQuery('');
              } else {
                toast({ title: 'No task matches', description: 'Try scanning again or check the filter (must not be Completed).' });
              }
            }
          }}
          placeholder="Type or paste code (Enter)"
          className="bg-white/10 border border-white/20 rounded-md px-2 py-1.5 text-xs w-52 placeholder:text-white/40 focus:outline-none focus:border-emerald-400"
          data-testid="tasks-global-search"
        />
        <button
          type="button"
          onClick={() => setGlobalScan(true)}
          className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold px-3 py-1.5 rounded-md flex items-center gap-1.5"
          data-testid="tasks-global-scan-btn"
        >
          <ScanLine className="w-3.5 h-3.5" /> Scan camera
        </button>
      </div>

      <div className="flex items-end gap-3 flex-wrap mb-3">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wide text-neutral-500 mb-1">Type</label>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="h-9 text-sm border border-neutral-300 rounded-md px-2" data-testid="tasks-filter-type">
            <option value="">All</option>
            <option value="picking">Picking</option>
            <option value="putaway">Put-away</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wide text-neutral-500 mb-1">Status</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-9 text-sm border border-neutral-300 rounded-md px-2" data-testid="tasks-filter-status">
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        {isManager && (
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-neutral-500 mb-1">Assignee</label>
            <select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)} className="h-9 text-sm border border-neutral-300 rounded-md px-2 min-w-[180px]" data-testid="tasks-filter-assignee">
              <option value="">All</option>
              {users.map((u) => <option key={u.email} value={u.email}>{u.name} ({u.role})</option>)}
            </select>
          </div>
        )}
        {/* Feb 2026 v11 — Order filter, searchable + scannable.
            Populated from the orders actually referenced by the
            tasks in the current pod so the picker never shows an
            order with zero tasks. Feb 2026 v12 — swapped the plain
            <select> for a searchable combobox + camera-scan button
            so a manager can either type an order number or scan
            the printed barcode on the Order Summary PDF. */}
        {isManager && (
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wide text-neutral-500 mb-1">Order</label>
            <OrderComboBox
              value={filterOrder}
              onChange={setFilterOrder}
              options={orderOptions}
              placeholder="Type or scan order #…"
              testId="tasks-filter-order"
              onScanMiss={(code) => toast({
                title: 'No matching order',
                description: `Scanned "${code}" — no task in this pod belongs to that order. Reset filter to see all orders.`,
              })}
            />
          </div>
        )}
        <div className="ml-auto text-xs text-neutral-500">
          Showing <span className="font-bold text-neutral-800">{tasks.length}</span> task{tasks.length === 1 ? '' : 's'}
        </div>
      </div>

      {/* Feb 2026 v13 — Order-view mode: when an order is selected we
          show a bulk "Assign all tasks to" dropdown right above the
          table so the manager can fan the whole pick list out to one
          picker in a single click. When no order is selected the
          whole table is hidden — see the empty state below. */}
      {isManager && filterOrder && (
        <div
          className="mb-3 p-3 rounded-xl bg-blue-50 border border-blue-200 flex flex-wrap items-center gap-3"
          data-testid="tasks-bulk-assign-bar"
        >
          <div className="text-sm text-blue-900 font-semibold">
            Assign all {tasks.length} task{tasks.length === 1 ? '' : 's'} on this order to:
          </div>
          <select
            value={(tasks.every((t) => (t.assignee_email || '') === (tasks[0]?.assignee_email || '')) && tasks[0]?.assignee_email) || ''}
            onChange={(e) => bulkAssignSelectedOrder(e.target.value)}
            className="h-9 text-sm border border-blue-300 rounded-md px-2 min-w-[220px] bg-white focus:border-[#0078D4] focus:outline-none focus:ring-1 focus:ring-[#0078D4]"
            data-testid="tasks-bulk-assign-select"
          >
            <option value="">— Choose staff member (or clear) —</option>
            {users.map((u) => (
              <option key={u.email} value={u.email}>
                {u.name} · {u.role === 'floor_incharge' ? 'Floor In-charge' : (u.role || 'picker')}
              </option>
            ))}
          </select>
          <div className="text-[11px] text-blue-800">
            Selecting a person applies to every task in this order in one go.
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-16 flex justify-center text-neutral-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (isManager && !filterOrder) ? (
        /* Feb 2026 v13 — Task list is hidden until the manager
           picks an order (or scans one) from the Order dropdown.
           Keeps the tab from feeling like a firehose of every
           picking + putaway task in the warehouse. */
        <div
          className="py-16 text-center text-sm text-neutral-500"
          data-testid="tasks-empty-pick-order"
        >
          <ClipboardCheck className="w-10 h-10 mx-auto mb-3 text-neutral-300" />
          <div className="font-semibold text-neutral-700 mb-1">Pick an order to see its tasks</div>
          <div className="text-xs italic max-w-md mx-auto">
            Select an order from the <span className="font-semibold">Order</span> dropdown above (or tap Scan and point the camera at the barcode on the Order Summary PDF) to view its picking &amp; put-away tasks.
          </div>
        </div>
      ) : tasks.length === 0 ? (
        <div className="py-16 text-center text-sm text-neutral-500 italic" data-testid="tasks-empty">
          No tasks match the current filters. New picking tasks appear when an order is confirmed; put-away tasks appear when placements are created.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tasks-table">
            <thead className="bg-neutral-50 text-[10px] uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="text-left px-2 py-2">Type</th>
                <th className="text-left px-2 py-2">Product / Order</th>
                <th className="text-right px-2 py-2">Qty</th>
                <th className="text-left px-2 py-2">Prod / Exp</th>
                <th className="text-left px-2 py-2">Zone / Loc / Pallet</th>
                <th className="text-left px-2 py-2">Assignee</th>
                <th className="text-left px-2 py-2">Status</th>
                <th className="text-left px-2 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr key={t.id} className="border-t border-neutral-100 align-top" data-testid={`task-row-${t.id}`}>
                  <td className="px-2 py-2">{typePill(t.type)}</td>
                  <td className="px-2 py-2">
                    <div className="font-medium text-[#0F172A]">{t.product_name || t.product_id}</div>
                    {t.order_number && <div className="text-[10px] text-neutral-500 font-mono">Order {t.order_number}</div>}
                    {t.warehouse_name && <div className="text-[10px] text-neutral-400">@ {t.warehouse_name}</div>}
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums font-semibold">{t.qty} <span className="text-[10px] text-neutral-400 font-normal">{t.uom}</span></td>
                  <td className="px-2 py-2 font-mono text-[11px]">
                    {formatDate(t.production_date) || '—'}
                    {t.expiry_date && <div className="text-neutral-400">exp {formatDate(t.expiry_date)}</div>}
                  </td>
                  <td className="px-2 py-2 font-mono text-[11px]">
                    {t.zone || '—'} / {t.location_id || '—'} / {t.pallet_id || '—'}
                  </td>
                  <td className="px-2 py-2">
                    {/* Feb 2026 v13 — per-row assignee dropdown is
                        hidden when we're in order-view mode (i.e.
                        the manager selected an order) because the
                        bulk "Assign all tasks to" bar above takes
                        precedence. Row still shows read-only
                        assignee text so the manager can see who
                        currently holds the task. */}
                    {isManager && !filterOrder ? (
                      <select
                        value={t.assignee_email || ''}
                        onChange={(e) => patchTask(t, { assignee_email: e.target.value })}
                        className="h-8 text-xs border border-neutral-300 rounded px-1 min-w-[150px]"
                        data-testid={`task-assign-${t.id}`}
                      >
                        <option value="">Unassigned</option>
                        {users.map((u) => (
                          <option key={u.email} value={u.email}>
                            {u.name} · {u.role === 'floor_incharge' ? 'Floor In-charge' : (u.role || 'picker')}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-xs text-neutral-700" data-testid={`task-assignee-readonly-${t.id}`}>
                        {t.assignee_name || t.assignee_email || 'Unassigned'}
                      </span>
                    )}
                    {(t.assignee_name || t.assignee_email) && (
                      <div className="text-[10px] text-neutral-500 mt-1" data-testid={`task-assignee-meta-${t.id}`}>
                        <span className="font-medium text-neutral-700">{t.assignee_name || t.assignee_email}</span>
                        {t.assigned_at && <> · assigned {formatDateTime(t.assigned_at)}</>}
                      </div>
                    )}
                    {t.trigger && t.trigger !== 'manual' && t.trigger !== 'order_confirmed' && (
                      <span
                        className={`inline-block mt-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                          t.trigger === 'bulk_upload' ? 'bg-purple-100 text-purple-800' :
                          t.trigger === 'return' ? 'bg-orange-100 text-orange-800' :
                          t.trigger === 'manual_stage' ? 'bg-amber-100 text-amber-800' :
                          'bg-neutral-100 text-neutral-700'
                        }`}
                        title={t.notes || ''}
                        data-testid={`task-trigger-${t.id}`}
                      >
                        {t.trigger === 'bulk_upload' ? 'Bulk upload' :
                         t.trigger === 'return' ? 'Return' :
                         t.trigger === 'manual_stage' ? 'Stage' :
                         t.trigger.replace('_', ' ')}
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <select
                      value={t.status}
                      onChange={(e) => patchTask(t, { status: e.target.value })}
                      className="h-8 text-xs border border-neutral-300 rounded px-1"
                      disabled={!isManager && (t.assignee_email || '').toLowerCase() !== (me?.email || '').toLowerCase()}
                      data-testid={`task-status-${t.id}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In progress</option>
                      {/* Feb 2026 — Loading tasks must be finished via the
                          proof-of-load photo-upload flow (Loading tab).
                          Hiding the "Completed" option here forces the
                          desk to use the correct path so we never end
                          up with a completed-but-photoless loading
                          record. The backend `update_warehouse_task`
                          endpoint 400s the same case as a belt+braces. */}
                      {t.type !== 'loading' && (
                        <option value="completed">Completed</option>
                      )}
                    </select>
                    <div className="mt-1">{statusPill(t.status)}</div>
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex flex-col gap-1">
                      {t.status !== 'completed' && (
                        <button
                          type="button"
                          onClick={() => setScanTask(t)}
                          className="text-emerald-600 hover:text-emerald-800 text-xs font-semibold flex items-center gap-1"
                          data-testid={`task-scan-${t.id}`}
                        >
                          <ScanLine className="w-3.5 h-3.5" /> Scan
                        </button>
                      )}
                      {isManager && (
                        <button
                          type="button"
                          onClick={() => removeTask(t)}
                          className="text-red-500 hover:text-red-700 text-xs font-semibold"
                          data-testid={`task-delete-${t.id}`}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Camera scanner tied to a specific task */}
      <WarehouseTaskScanner
        open={!!scanTask}
        task={scanTask}
        authHeaders={authHeaders}
        onClose={() => setScanTask(null)}
        onCompleted={(updated) => {
          setTasks((prev) => prev.map((t) => (t.id === updated.id ? updated : t)));
        }}
      />

      {/* Global camera scan → decodes any barcode + auto-selects matching task */}
      {globalScan && (
        <GlobalTaskScanner
          tasks={tasks}
          onClose={() => setGlobalScan(false)}
          onPicked={(t) => { setGlobalScan(false); setScanTask(t); }}
        />
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// GlobalTaskScanner — decodes a code, matches against task pallet_id /
// location_id / product SKU, and hands off to the per-task scanner.
// -----------------------------------------------------------------------------
const GlobalTaskScanner = ({ tasks, onClose, onPicked }) => {
  const videoRef = React.useRef(null);
  const controlsRef = React.useRef(null);
  const [status, setStatus] = useState('starting');
  const [errMsg, setErrMsg] = useState('');
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // Lazy-load @zxing so the rest of the tab renders instantly.
    (async () => {
      try {
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        const { BarcodeFormat, DecodeHintType } = await import('@zxing/library');
        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.CODE_39, BarcodeFormat.CODE_128, BarcodeFormat.QR_CODE,
        ]);
        const reader = new BrowserMultiFormatReader(hints);
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' } } },
          videoRef.current,
          (result) => {
            if (cancelled) return;
            if (result) {
              const text = String(result.getText() || '').trim().toUpperCase();
              const hit = tasks.find((t) => (
                (t.pallet_id || '').toUpperCase() === text ||
                (t.location_id || '').toUpperCase() === text ||
                (t.product_id || '').toUpperCase() === text
              ) && t.status !== 'completed');
              if (hit) {
                try { controls.stop(); } catch { /* noop */ }
                onPicked(hit);
              } else {
                setBanner({ kind: 'error', message: `No open task matches ${text}. Keep scanning…` });
              }
            }
          },
        );
        controlsRef.current = controls;
        if (!cancelled) setStatus('scanning');
      } catch (err) {
        if (cancelled) return;
        setErrMsg(err?.message || 'Camera failed.');
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      try { controlsRef.current?.stop(); } catch { /* noop */ }
      controlsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center px-4" data-testid="tasks-global-scanner">
      <div className="relative w-full max-w-md bg-[#0F172A] rounded-3xl overflow-hidden shadow-2xl">
        <div className="flex items-center justify-between px-4 pt-4 pb-2 text-white">
          <div className="flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-emerald-400" />
            <span className="font-display text-base">Scan any pallet / item / location</span>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
            data-testid="tasks-global-scanner-close"
          >
            <XIcon className="w-4 h-4 text-white" />
          </button>
        </div>
        {banner && (
          <div className="mx-4 mb-2 px-3 py-2 rounded-lg text-sm font-medium bg-red-500/20 text-red-100 border border-red-400/40" data-testid="global-scan-banner-error">
            {banner.message}
          </div>
        )}
        <div className="relative aspect-[4/3] bg-black">
          <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
          {status === 'starting' && (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Starting camera…
            </div>
          )}
          {status === 'error' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center text-white text-sm">
              {errMsg}
            </div>
          )}
        </div>
        <div className="p-3 text-center text-white/70 text-xs">
          Aim at any barcode belonging to an open task — the scanner will auto-open the correct verification flow.
        </div>
      </div>
    </div>
  );
};
